import { useCallback } from 'react'
import { useSnapshot } from 'valtio'
import type { RadarConfig } from '../../../../types'
import { api } from '../../lib/api'
import { state } from '../../lib/state'
import { logError } from '@/components/Error/Error'

/**
 * SavedCheckmark
 *
 * A reusable component to display a green checkmark on the right side of an input box after saving.
 *
 * Usage:
 *   <div style={{ position: 'relative', display: 'inline-block' }}>
 *     <input ... />
 *     <SavedCheckmark isSaved={isSaved} />
 *   </div>
 *
 * The parent container must have 'position: relative' and 'display: inline-block' or 'block'.
 * The checkmark will be absolutely positioned and will not affect the input's size.
 *
 * Props:
 *   isSaved: boolean - whether to show the checkmark
 */
export const SavedCheckmark: React.FC<{ isSaved: boolean }> = ({ isSaved }) => (
  <span
    style={{
      position: 'absolute',
      right: 8,
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
      opacity: isSaved ? 1 : 0,
      transition: 'opacity 0.2s',
      color: '#27ae60', // Green
      fontSize: 20,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center'
    }}
  >
    {/* SVG checkmark icon */}
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Saved</title>
      <circle cx="10" cy="10" r="10" fill="#27ae60" fillOpacity="0.15" />
      <path
        d="M6 10.5L9 13.5L14 8.5"
        stroke="#27ae60"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
)

/**
 * Hook to return a setConfig function that persists config changes to the backend and updates valtio state.
 * Usage: const setConfig = usePersistedConfig(setLocalConfig)
 */
export function usePersistedConfig(setLocalConfig: (c: RadarConfig) => void) {
  const { currentRadarId } = useSnapshot(state)
  const setConfig = useCallback(
    async (updatedConfig: RadarConfig) => {
      setLocalConfig(updatedConfig)
      try {
        if (!currentRadarId) {
          throw new Error('Cannot save config: currentRadarId is missing.')
        }
        if (!updatedConfig.version) {
          updatedConfig.version = '1'
        }
        const savedConfig = await api.setConfig(
          { ...updatedConfig, version: updatedConfig.version },
          Number(currentRadarId)
        )
        // Update valtio global state
        if (state.radarConfig) {
          Object.assign(state.radarConfig, savedConfig)
        }

        // IMPORTANT: Also update the corresponding radar's config in availableRadars
        // This prevents stale config data when navigating between pages
        if (state.availableRadars) {
          const radarIndex = state.availableRadars.findIndex(
            r => Number(r.id) === Number(currentRadarId)
          )
          if (radarIndex !== -1) {
            console.info(
              'usePersistedConfig',
              'Updating availableRadars config',
              {
                currentRadarId,
                radarIndex,
                quadrantTitles: savedConfig.quadrants?.map(q => q.title),
                ringTitles: savedConfig.rings?.map(r => r.title)
              }
            )
            // Update the config for this specific radar in the availableRadars array
            state.availableRadars[radarIndex] = {
              ...state.availableRadars[radarIndex],
              config: savedConfig
            }
          } else {
            console.info(
              'usePersistedConfig',
              'Radar not found in availableRadars',
              {
                currentRadarId,
                availableRadarIds: state.availableRadars.map(r => r.id)
              }
            )
          }
        }
      } catch (e) {
        logError('AdminRadarListPage', 'Failed to persist config:', e)
      }
    },
    [setLocalConfig, currentRadarId]
  )
  return setConfig
}
